import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const CallingSection = ({ darkMode }) => {
  const [callingData, setCallingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, -100]);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    loadCallingData();
  }, []);

  const loadCallingData = async () => {
    try {
      const docRef = doc(db, 'callingSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setCallingData(docSnap.data());
      } else {
        // Fallback to default data
        setCallingData({
          title: 'The Calling',
          subtitle: 'Divine Calling & Preparation',
          quote: '"In 2001, I found a deep-seated passion for the lost. God\'s economy and plan led me to join the International School of Missions."',
          content: `From a Muslim background and a lineage that practiced witchcraft, my journey to Christ is a testament to God's sheer grace. I am a first-generation Christian who accepted the Lord as my personal savior in 1995 and was filled by the Holy Spirit in 1998.

Unlike many who become pew warmers after salvation, I immediately felt the necessity to serve. From the Pastoral Programme Initiative in primary school to the Christian Union in high school, I served under mentors who saw my potential and took me in as a partner for gospel advancement.

Through the guidance of mentors and under the tutelage of ArchBishop Henry Z. Mulandi at the International School of Missions, my passion for ministry was chiseled through practical experiences that went beyond transformative lectures. This training became the foundation for my future church planting missions across Kenya and beyond.

The calling wasn't just about preaching—it was about being available for service anywhere, anytime. Whether in the worship team or out for evangelism, this new-found faith compelled me to expend myself in different departments for God's kingdom.`
        });
      }
    } catch (err) {
      console.error('Error loading calling section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className={`absolute inset-0 z-10 ${
            darkMode 
              ? 'bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent' 
              : 'bg-gradient-to-r from-white via-white/40 to-transparent'
          }`} />
          <img 
            src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1600&h=1200&fit=crop" 
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div ref={ref} className="max-w-5xl mx-auto px-8 relative z-20">
          <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-12 md:p-16 rounded-3xl shadow-2xl space-y-6`}>
            <div className="text-center">
              <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Loading Calling Section...
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className={`absolute inset-0 z-10 ${
            darkMode 
              ? 'bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent' 
              : 'bg-gradient-to-r from-white via-white/40 to-transparent'
          }`} />
          <img 
            src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1600&h=1200&fit=crop" 
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-5xl mx-auto px-8 relative z-20 text-center">
          <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-12 md:p-16 rounded-3xl shadow-2xl`}>
            <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use the actual data from Firestore
  const data = callingData;

  // Process content - split by paragraphs
  const renderContent = () => {
    if (!data.content) return <p>No content available.</p>;

    // Split content by double newlines to create paragraphs
    const paragraphs = data.content.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((paragraph, index) => (
      <p key={index} className="mb-6 last:mb-0">
        {paragraph}
      </p>
    ));
  };

  return (
    <section className="relative min-h-screen flex items-center py-32 overflow-hidden">
      {/* Background Image - Fixed and separate from content */}
      <motion.div 
        style={{ y: y1 }}
        className="absolute inset-0 z-0"
      >
        <div className={`absolute inset-0 z-10 ${
          darkMode 
            ? 'bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent' 
            : 'bg-gradient-to-r from-white via-white/40 to-transparent'
        }`} />
        <img 
          src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1600&h=1200&fit=crop" 
          alt="Background"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Content Card - Separate from background */}
      <div ref={ref} className="max-w-5xl mx-auto px-8 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-12 md:p-16 rounded-3xl shadow-2xl`}
        >
          {/* Subtitle from DB */}
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-6`}>
            {data.subtitle}
          </div>
          
          {/* Main Title from DB */}
          <h2 className={`text-5xl md:text-6xl font-bold mb-8 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {data.title}
          </h2>
          
          {/* Quote from DB */}
          {data.quote && (
            <blockquote className={`text-3xl font-light ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-8 leading-relaxed italic ${darkMode ? 'border-l-4 border-white' : 'border-l-4 border-slate-900'} pl-8`}>
              {data.quote}
            </blockquote>
          )}
          
          {/* Content from DB */}
          <div className={`space-y-6 text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed`}>
            {renderContent()}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CallingSection;